/**
 * WorkspaceBusinessRules — workspace-scoped Business-Rule Discovery page (Iter 36).
 *
 * Surfaces ``GET /api/workspaces/{id}/business-rules`` (paginated list) and
 * ``GET .../clusters`` (rollups) into a single dashboard so consultants can
 * filter every IF/WHEN/CASE/DOW decision their RPG estate makes, clustered
 * by file or field for impact-analysis questions like:
 *   "Show me every rule that touches ORDER.STATUS — across all programs."
 *
 * The data is populated automatically when consultants parse an RPG program
 * via the Source Parser tab — they don't need to invoke a separate ingest.
 * The "Rebuild" button on this page is a manual escape hatch for re-running
 * the extractor across all stored programs (e.g. after the noise filter is
 * tuned).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  CircleNotchIcon as Spinner,
  GitBranchIcon as GitBranch,
  FunnelIcon as Funnel,
  ArrowLeftIcon as ArrowLeft,
  ArrowsClockwiseIcon as ArrowsClockwise,
  CodeIcon as Code,
  ListNumbersIcon as ListNumbers,
  StackIcon as Stack,
  SealCheckIcon as SealCheck,
} from "@phosphor-icons/react";
import {
  bulkDecideBusinessRules,
  decideBusinessRule,
  getBusinessRuleClusters,
  listBusinessRules,
  listWorkspaceMembers,
  rebuildBusinessRules,
} from "@/lib/api";
import { formatApiErrorDetail, useAuth } from "@/lib/auth";
import { stateMeta } from "@/lib/validationConstants";
import ValidationBadge from "@/components/validation/ValidationBadge";
import DecisionButtons from "@/components/validation/DecisionButtons";
import RuleStateChips from "@/components/businessrules/RuleStateChips";
import RuleBulkBar from "@/components/businessrules/RuleBulkBar";

const PAGE = 100;

// Visual styling per rule_kind so the data table scans at a glance.
const KIND_COLOR = {
  if:        { bg: "#edf5ff", fg: "#0043ce", border: "#a6c8ff" },
  elseif:    { bg: "#edf5ff", fg: "#0043ce", border: "#a6c8ff" },
  else:      { bg: "#f4f4f4", fg: "#525252", border: "#c6c6c6" },
  select:    { bg: "#fff8e1", fg: "#8a6c00", border: "#f1c21b" },
  when:      { bg: "#fff8e1", fg: "#8a6c00", border: "#f1c21b" },
  otherwise: { bg: "#f4f4f4", fg: "#525252", border: "#c6c6c6" },
  case:      { bg: "#defbe6", fg: "#0e6027", border: "#a7f0ba" },
  dow:       { bg: "#f6f2ff", fg: "#6929c4", border: "#d4bbff" },
  dou:       { bg: "#f6f2ff", fg: "#6929c4", border: "#d4bbff" },
};

function KindBadge({ kind }) {
  const c = KIND_COLOR[kind] || { bg: "#f4f4f4", fg: "#525252", border: "#c6c6c6" };
  return (
    <span
      className="ml-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border whitespace-nowrap"
      style={{ background: c.bg, color: c.fg, borderColor: c.border }}
      data-testid={`br-kind-${kind}`}
    >
      {kind}
    </span>
  );
}

function ChipList({ items, kind, onChipClick, activeValue }) {
  if (!items?.length) return <span className="text-[var(--ml-text-secondary)]">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => onChipClick?.(v)}
          data-testid={`br-chip-${kind}-${v}`}
          className={`ml-mono text-[10px] px-1.5 py-0.5 border transition-colors ${
            activeValue === v
              ? "border-[var(--ml-primary)] bg-[var(--ml-primary)]/10 text-[var(--ml-primary)]"
              : "border-[var(--ml-border)] hover:border-[var(--ml-primary)]/60"
          }`}
        >
          {v}
        </button>
      ))}
    </div>
  );
}

function StatCard({ label, value, Icon, color, testid }) {
  return (
    <div className="ml-card p-3" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon size={16} weight="bold" style={{ color }} />
        <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
          {label}
        </span>
      </div>
      <div className="text-2xl font-semibold tabular-nums mt-1" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

function ClusterPanel({ title, rows, columns, kind, onSelect, activeValue }) {
  if (!rows?.length) return null;
  return (
    <div className="ml-card" data-testid={`br-cluster-${kind}`}>
      <div className="px-3 py-2 border-b border-[var(--ml-border)] ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
        {title} · {rows.length}
      </div>
      <table className="w-full text-sm">
        <thead className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] bg-[var(--ml-surface)]/50">
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={`px-3 py-2 ${c.numeric ? "text-right" : "text-left"}`}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r[columns[0].key]}
              onClick={() => onSelect?.(r[columns[0].key])}
              data-testid={`br-cluster-${kind}-row-${r[columns[0].key]}`}
              data-active={activeValue === r[columns[0].key]}
              className={`border-b last:border-b-0 border-[var(--ml-border)] cursor-pointer hover:bg-[var(--ml-surface)]/40 ${
                activeValue === r[columns[0].key] ? "bg-[var(--ml-primary)]/10" : ""
              }`}
            >
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={`px-3 py-2 ${c.numeric ? "ml-mono text-right tabular-nums" : c.mono ? "ml-mono font-semibold" : ""}`}
                >
                  {r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function WorkspaceBusinessRules() {
  const { id: workspaceId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [clusters, setClusters] = useState(null);
  const [data, setData] = useState({ total: 0, rows: [] });
  const [loading, setLoading] = useState(true);
  const [rebuilding, setRebuilding] = useState(false);
  const [myRole, setMyRole] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [deciding, setDeciding] = useState(null); // rule id | "bulk" | null
  const [filters, setFilters] = useState(() => ({
    q: "",
    program: (searchParams.get("program") || "").toUpperCase(),
    kind: "",
    file: "",
    field: (searchParams.get("field") || "").toUpperCase(),
    state: "",
    offset: 0,
  }));

  const canEdit = user?.role === "admin" || myRole === "owner" || myRole === "editor";

  useEffect(() => {
    listWorkspaceMembers(workspaceId)
      .then((m) => setMyRole((m.members || []).find((x) => x.user_id === user?.id)?.role || null))
      .catch(() => setMyRole(null));
  }, [workspaceId, user]);

  const refreshClusters = useCallback(async () => {
    try {
      const c = await getBusinessRuleClusters(workspaceId);
      setClusters(c);
    } catch {
      setClusters(null);
    }
  }, [workspaceId]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listBusinessRules(workspaceId, {
        limit: PAGE,
        offset: filters.offset,
        q: filters.q || undefined,
        program: filters.program || undefined,
        kind: filters.kind || undefined,
        file: filters.file || undefined,
        field: filters.field || undefined,
        state: filters.state || undefined,
      });
      setData(r);
    } finally {
      setLoading(false);
    }
  }, [
    workspaceId, filters.offset, filters.q, filters.program,
    filters.kind, filters.file, filters.field, filters.state,
  ]);

  useEffect(() => { refreshClusters(); }, [refreshClusters]);
  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { setSelected(new Set()); }, [filters]);

  const pageInfo = useMemo(() => {
    const start = filters.offset + 1;
    const end = Math.min(filters.offset + data.rows.length, data.total);
    return { start: data.rows.length ? start : 0, end, total: data.total };
  }, [filters.offset, data.rows.length, data.total]);

  const handleRebuild = async () => {
    if (!window.confirm("Re-extract business rules across every parsed RPG program in this workspace? Rule rows are replaced; validation decisions are carried over where the rule's condition is unchanged.")) return;
    setRebuilding(true);
    try {
      const r = await rebuildBusinessRules(workspaceId);
      const carry = r.validation_carried || r.validation_dropped
        ? ` Decisions carried: ${r.validation_carried}, dropped: ${r.validation_dropped}.`
        : "";
      toast.success(`Rebuilt ${r.programs_rebuilt} program${r.programs_rebuilt === 1 ? "" : "s"} → ${r.rules_extracted} rules.${carry}`);
      refreshClusters();
      fetchList();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Rebuild failed");
    } finally {
      setRebuilding(false);
    }
  };

  const afterDecision = (decision, n) => {
    const verb = decision === "proposed" ? "Reopened" : decision === "validated" ? "Validated" : "Rejected";
    toast.success(`${verb} ${n} rule${n === 1 ? "" : "s"}.`);
    refreshClusters();
    fetchList();
  };

  const decideOne = async (rule, decision) => {
    setDeciding(rule.id);
    try {
      const updated = await decideBusinessRule(workspaceId, rule.id, decision);
      setData((d) => ({ ...d, rows: d.rows.map((r) => (r.id === updated.id ? updated : r)) }));
      afterDecision(decision, 1);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Decision failed");
    } finally {
      setDeciding(null);
    }
  };

  const decideBulk = async (decision, note) => {
    setDeciding("bulk");
    try {
      const r = await bulkDecideBusinessRules(workspaceId, Array.from(selected), decision, note);
      setSelected(new Set());
      afterDecision(decision, r.updated);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Bulk decision failed");
    } finally {
      setDeciding(null);
    }
  };

  const toggleRow = (id) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allOnPageSelected = data.rows.length > 0 && data.rows.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected(allOnPageSelected ? new Set() : new Set(data.rows.map((r) => r.id)));

  const noData = !loading && clusters && clusters.totals.rules === 0;
  const byState = clusters?.by_state || {};
  const hasFilters = filters.q || filters.program || filters.kind || filters.file || filters.field || filters.state;

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 space-y-4" data-testid="business-rules-page">
      <div className="flex items-center justify-between">
        <Link
          to={`/workspaces/${workspaceId}/metadata/source-parser`}
          className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] hover:text-[var(--ml-text-primary)] inline-flex items-center gap-1"
          data-testid="br-back"
        >
          <ArrowLeft size={11} weight="bold" /> Source parser
        </Link>
        <button
          type="button"
          onClick={handleRebuild}
          disabled={rebuilding}
          data-testid="br-rebuild-btn"
          className="ml-btn-secondary text-xs inline-flex items-center gap-1"
          title="Re-extract business rules across every parsed RPG program"
        >
          {rebuilding ? <Spinner size={11} className="animate-spin" /> : <ArrowsClockwise size={11} weight="bold" />}
          Rebuild
        </button>
      </div>

      <div>
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
          <GitBranch size={20} weight="duotone" /> Business-Rule Discovery
        </h1>
        <p className="text-sm text-[var(--ml-text-secondary)] mt-1 max-w-3xl">
          Every IF / WHEN / CASE / DOW / DOU decision your RPG estate makes —
          clustered by file or field so impact-analysis questions like
          &ldquo;everywhere we touch <code className="ml-mono text-xs">ORDER.STATUS</code>&rdquo;
          are one click away. Auto-populated when you parse a program from the
          Source Parser tab. Rules start as <ValidationBadge state="proposed" testid="br-legend-inference" className="align-middle" /> —
          validate the ones that are real business logic so only reviewed knowledge reaches client reports and the Knowledge Base.
        </p>
      </div>

      {noData && (
        <div className="ml-card p-10 text-center" data-testid="br-empty">
          <GitBranch size={36} weight="duotone" className="text-[var(--ml-text-secondary)] inline mb-3" />
          <div className="text-base font-semibold mb-1">No business rules extracted yet</div>
          <p className="text-sm text-[var(--ml-text-secondary)] max-w-md mx-auto">
            Parse an RPG program from the
            <Link to={`/workspaces/${workspaceId}/metadata/source-parser`} className="text-[var(--ml-primary)] hover:underline mx-1">
              Source Parser
            </Link>
            tab — rules are extracted automatically.
          </p>
        </div>
      )}

      {!noData && clusters && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="br-summary">
            <StatCard label="Rules" value={clusters.totals.rules} Icon={GitBranch} color="#0f62fe" testid="br-total-rules" />
            <StatCard label="Validated" value={byState.validated || 0} Icon={SealCheck} color={stateMeta("validated").fg} testid="br-total-validated" />
            <StatCard label="Programs" value={clusters.totals.programs} Icon={Code} color="#6929c4" testid="br-total-programs" />
            <StatCard label="Files referenced" value={clusters.totals.files} Icon={Stack} color="#0e6027" testid="br-total-files" />
            <StatCard label="Fields referenced" value={clusters.totals.fields} Icon={ListNumbers} color="#8a6c00" testid="br-total-fields" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ClusterPanel
              title="Rules by file"
              kind="file"
              rows={clusters.by_file?.slice(0, 12) || []}
              columns={[
                { key: "file", label: "File", mono: true },
                { key: "rule_count", label: "Rules", numeric: true },
                { key: "program_count", label: "Programs", numeric: true },
              ]}
              onSelect={(v) => setFilters((f) => ({ ...f, file: f.file === v ? "" : v, offset: 0 }))}
              activeValue={filters.file}
            />
            <ClusterPanel
              title="Rules by field"
              kind="field"
              rows={clusters.by_field?.slice(0, 12) || []}
              columns={[
                { key: "field", label: "Field", mono: true },
                { key: "rule_count", label: "Rules", numeric: true },
                { key: "program_count", label: "Programs", numeric: true },
              ]}
              onSelect={(v) => setFilters((f) => ({ ...f, field: f.field === v ? "" : v, offset: 0 }))}
              activeValue={filters.field}
            />
          </div>

          <div className="ml-card p-3 flex flex-wrap items-center gap-2" data-testid="br-filters">
            <RuleStateChips
              byState={byState}
              total={clusters.totals.rules}
              active={filters.state}
              onChange={(v) => setFilters((f) => ({ ...f, state: v, offset: 0 }))}
            />
            <div className="relative flex-1 min-w-[200px]">
              <input
                type="text"
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value, offset: 0 }))}
                placeholder="Search condition text"
                data-testid="br-search"
                className="ml-mono text-xs w-full px-2 py-1.5 border border-[var(--ml-border)] focus:outline-none focus:border-[var(--ml-primary)]"
              />
            </div>
            <select
              value={filters.kind}
              onChange={(e) => setFilters((f) => ({ ...f, kind: e.target.value, offset: 0 }))}
              data-testid="br-filter-kind"
              className="ml-mono text-xs px-2 py-1.5 border border-[var(--ml-border)]"
            >
              <option value="">All kinds</option>
              {(clusters.by_kind || []).map((k) => (
                <option key={k.kind} value={k.kind}>{k.kind} ({k.count})</option>
              ))}
            </select>
            <select
              value={filters.program}
              onChange={(e) => setFilters((f) => ({ ...f, program: e.target.value, offset: 0 }))}
              data-testid="br-filter-program"
              className="ml-mono text-xs px-2 py-1.5 border border-[var(--ml-border)]"
            >
              <option value="">All programs</option>
              {(clusters.by_program || []).map((p) => (
                <option key={p.program_name} value={p.program_name}>{p.program_name} ({p.rule_count})</option>
              ))}
            </select>
            {hasFilters && (
              <button
                type="button"
                onClick={() => setFilters({ q: "", program: "", kind: "", file: "", field: "", state: "", offset: 0 })}
                data-testid="br-clear-filters"
                className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-primary)] hover:underline px-2 inline-flex items-center gap-1"
              >
                <Funnel size={11} weight="bold" /> Clear ×
              </button>
            )}
          </div>

          {canEdit && (
            <RuleBulkBar
              count={selected.size}
              busy={deciding === "bulk"}
              onDecide={decideBulk}
              onClear={() => setSelected(new Set())}
            />
          )}

          <div className="flex items-center justify-between">
            <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]" data-testid="br-pageinfo">
              Showing {pageInfo.start}–{pageInfo.end} of {pageInfo.total}
            </div>
            <div className="flex gap-1">
              <button
                type="button"
                disabled={filters.offset === 0}
                onClick={() => setFilters((f) => ({ ...f, offset: Math.max(0, f.offset - PAGE) }))}
                data-testid="br-prev"
                className="px-3 py-1.5 text-xs border border-[var(--ml-border)] disabled:opacity-40 hover:bg-[var(--ml-surface)]/40"
              >
                Prev
              </button>
              <button
                type="button"
                disabled={filters.offset + PAGE >= data.total}
                onClick={() => setFilters((f) => ({ ...f, offset: f.offset + PAGE }))}
                data-testid="br-next"
                className="px-3 py-1.5 text-xs border border-[var(--ml-border)] disabled:opacity-40 hover:bg-[var(--ml-surface)]/40"
              >
                Next
              </button>
            </div>
          </div>

          {loading ? (
            <div className="ml-card p-8 text-center text-sm text-[var(--ml-text-secondary)]" data-testid="br-loading">
              <Spinner size={14} className="animate-spin inline mr-2" /> Loading rules…
            </div>
          ) : data.rows.length === 0 ? (
            <div className="ml-card p-8 text-center text-sm text-[var(--ml-text-secondary)]" data-testid="br-no-results">
              No rules match the current filters.
            </div>
          ) : (
            <div className="ml-card overflow-x-auto" data-testid="br-table">
              <table className="w-full text-sm">
                <thead className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] bg-[var(--ml-surface)]/50">
                  <tr>
                    {canEdit && (
                      <th className="px-3 py-2 text-left w-8">
                        <input
                          type="checkbox"
                          checked={allOnPageSelected}
                          onChange={toggleAll}
                          aria-label="Select all rules on this page"
                          data-testid="br-select-all"
                        />
                      </th>
                    )}
                    <th className="px-3 py-2 text-left">Program</th>
                    <th className="px-3 py-2 text-right">Line</th>
                    <th className="px-3 py-2 text-left">Nest</th>
                    <th className="px-3 py-2 text-left">Kind</th>
                    <th className="px-3 py-2 text-left">Condition</th>
                    <th className="px-3 py-2 text-left">Files</th>
                    <th className="px-3 py-2 text-left">Fields</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    {canEdit && <th className="px-3 py-2 text-left">Review</th>}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr
                      key={r.id}
                      data-testid={`br-row-${r.id}`}
                      data-state={r.validation_state}
                      className={`border-b border-[var(--ml-border)] hover:bg-[var(--ml-surface)]/30 align-top ${
                        r.validation_state === "rejected" ? "opacity-60" : ""
                      } ${selected.has(r.id) ? "bg-[var(--ml-primary)]/5" : ""}`}
                    >
                      {canEdit && (
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={selected.has(r.id)}
                            onChange={() => toggleRow(r.id)}
                            aria-label={`Select rule ${r.id}`}
                            data-testid={`br-select-${r.id}`}
                          />
                        </td>
                      )}
                      <td className="px-3 py-2 ml-mono font-semibold whitespace-nowrap">{r.program_name}</td>
                      <td className="px-3 py-2 ml-mono text-right tabular-nums text-xs text-[var(--ml-text-secondary)]">{r.line_number}</td>
                      <td className="px-3 py-2 ml-mono text-xs">
                        <span style={{ paddingLeft: `${r.nest_level * 8}px` }}>
                          {r.nest_level === 0 ? "·" : "→".repeat(r.nest_level)}
                        </span>
                      </td>
                      <td className="px-3 py-2"><KindBadge kind={r.rule_kind} /></td>
                      <td className="px-3 py-2 ml-mono text-xs whitespace-pre-wrap break-words max-w-[420px]">
                        {r.condition_text || <span className="text-[var(--ml-text-secondary)] italic">(no condition)</span>}
                      </td>
                      <td className="px-3 py-2">
                        <ChipList
                          items={r.referenced_files}
                          kind="file"
                          onChipClick={(v) => setFilters((f) => ({ ...f, file: f.file === v ? "" : v, offset: 0 }))}
                          activeValue={filters.file}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <ChipList
                          items={r.referenced_fields}
                          kind="field"
                          onChipClick={(v) => setFilters((f) => ({ ...f, field: f.field === v ? "" : v, offset: 0 }))}
                          activeValue={filters.field}
                        />
                        {r.referenced_fields.length > 0 && (
                          <Link
                            to={`/workspaces/${workspaceId}/metadata/field-dependencies?program=${encodeURIComponent(r.program_name)}${r.referenced_fields.length === 1 ? `&impact=${encodeURIComponent(r.referenced_fields[0])}` : ""}`}
                            className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-primary)] hover:underline inline-block mt-1"
                            data-testid={`br-field-deps-${r.id}`}
                            title="Field-level dependencies for this program (Iter 41)"
                          >
                            field deps →
                          </Link>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <ValidationBadge state={r.validation_state} row={r} testid={`br-state-${r.id}`} />
                        {r.decided_by && (
                          <div className="ml-mono text-[10px] text-[var(--ml-text-secondary)] mt-1 whitespace-nowrap" data-testid={`br-decided-${r.id}`}>
                            {r.decided_by}
                          </div>
                        )}
                        {r.validation_note && (
                          <div className="text-[11px] text-[var(--ml-text-secondary)] mt-0.5 max-w-[200px] break-words" data-testid={`br-note-${r.id}`}>
                            {r.validation_note}
                          </div>
                        )}
                      </td>
                      {canEdit && (
                        <td className="px-3 py-2">
                          <DecisionButtons
                            state={r.validation_state}
                            compact
                            busy={deciding === r.id}
                            disabled={deciding !== null}
                            onDecide={(d) => decideOne(r, d)}
                            testidPrefix={`br-decision-${r.id}`}
                          />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
